import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import moment from 'moment';
import 'moment/locale/es';
import 'moment/locale/fr';
import {
  Avatar,
  Box,
  Card,
  CardHeader,
  Chip,
  Divider,
  Grid,
  Link,
  SvgIcon,
  Tooltip,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Paperclip as AttachmentsIcon } from 'react-feather';
import getInitials from 'src/utils/getInitials';
import { getClaimProduct } from 'src/utils/claim';
import ClaimsCardSkeleton from './ClaimsCardSkeleton';
import ClaimDisposition from '../../../components/ClaimDisposition';
import { useTranslation } from 'react-i18next';
moment.locale('en');

function formatLastUpdatedDate(date, locale = 'en') {
  if (!date) {
    return '';
  }

  const localeCode = locale.split('-')[0] || 'en';
  return moment(date).locale(localeCode).fromNow();
}

const useStyles = makeStyles(theme => ({
  root: {
    cursor: 'pointer',
    height: '100%'
  },
  cardStripe: {
    position: 'relative',
    color: theme.palette.common.white,
    background: theme.palette.secondary.main
  },
  actionNeededChip: {
    color: '#FF004E',
    backgroundColor: '#FFD4E7',
    fontWeight: 600,
    padding: theme.spacing(0.05)
  },
  avatar: {
    position: 'absolute',
    height: 50,
    width: 50,
    bottom: -20,
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.primary.main}`
  },
  claimant: {
    overflow: 'hidden'
  },
  gridRow: {
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  media: {
    height: 200,
    backgroundColor: theme.palette.background.dark
  },
  openStatus: {
    marginRight: -2,
    color: '#007913',
    backgroundColor: '#D4F9DC',

    '&:hover': {
      backgroundColor: '#D4F9DC',
    }
  },
  closedStatus: {
    marginRight: -2,
    color: '#006791',
    backgroundColor: '#D4EEF9',

    '&:hover': {
      backgroundColor: '#D4EEF9',
    }
  },
  subscribersIcon: {
    marginRight: theme.spacing(1)
  },
  attachmentsIcon: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1)
  }
}));

function ClaimsCard({ claim, className, isLoading, cardClickHandler, ...rest }) {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const getStatusLabelClass = status => {
    switch (status) {
      case 'Open':
        return classes.openStatus;
      case 'Closed':
        return classes.closedStatus;
      default:
        return '';
    }
  };

  const handleClickCard = () => {
    if (cardClickHandler) {
      cardClickHandler(claim);
    }
    else {
      history.push({
        pathname: '/glass/browse-claim',
        search: `?claimId=${claim.claimIdentifier}`,
      });
    }
  }

  if (isLoading) {
    return <ClaimsCardSkeleton claim={{}} />;
  }

  return (
    <Card
      className={clsx(classes.root, className)}
      {...rest}
      onClick={handleClickCard}
    >
      <CardHeader
        title={
          <Box display="flex" alignItems="center">
            <Link
              color="textPrimary"
              component={RouterLink}
              to={`/browse-claim/${claim.claimId}`}
              onClick={handleClickCard}
              variant="h5"
            >
              {claim.claimIdentifier}
            </Link>
            <Box flexGrow={1} />
            <Typography variant="body2" color="textSecondary">
              {t('updatedRelative', {
                timeAgo: formatLastUpdatedDate(claim.lastUpdateDate, i18n.language)
              })}
            </Typography>
          </Box>
        }
      />
      <Box p={2} className={classes.cardStripe}>
        {claim.claimantName && (
          <Avatar alt="Author" src={claim.avatar} className={classes.avatar}>
            {getInitials(claim.claimantName)}
          </Avatar>
        )}
        <Typography variant="body2" style={{ textAlign: 'right' }}>
          {claim.claimDisposition ?? ''}
        </Typography>
      </Box>
      <Box p={2}>
        <Grid
          className={classes.gridRow}
          alignItems="center"
          container
          justifyContent="space-between"
          spacing={3}>
          <Grid item xs={5}>
            <Box mt={2}>
              <Typography noWrap variant="h5" color="textPrimary">
                {claim.claimantName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('claimant')}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Box mt={2}>
              <Typography noWrap variant="h5" color="textPrimary" align="right">
                {claim.orderMasterControlNumber || ''}
              </Typography>
              <Typography
                noWrap
                variant="body2"
                color="textSecondary"
                align="right"
              >
                OMC #
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box p={2}>
        <Grid
          className={classes.gridRow}
          alignItems="center"
          direction="row"
          container
          justifyContent="space-between"
        >
          <Grid item xs={7}>
            <Typography noWrap variant="h5" color="textPrimary">
              {getClaimProduct(claim)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {t('type')}
            </Typography>
          </Grid>
          <Grid item xs={5}>
            <Box />
            <Box />
          </Grid>
        </Grid>
      </Box>
      <Box p={2}>
        <Grid
          className={classes.gridRow}
          alignItems="center"
          container
          justifyContent="space-between"
          spacing={3}
        >
          <Grid item xs={3}>
            <ClaimDisposition disposition={claim?.currentDisposition} textVariant='h5' />
          </Grid>
          <Grid item xs={9}>
            {claim.reviewer !== claim.claimantName && (
              <Tooltip title={claim.disablementDescription || claim.claimType || ''} arrow>

                <Typography noWrap variant="h5" color="textPrimary" align="right">
                  {claim.disablementDescription || claim.claimType}
                </Typography>
              </Tooltip>
            )}
            <Typography
              noWrap
              variant="body2"
              color="textSecondary"
              align="right"
            >
              {t('reason')}
            </Typography>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      <Box p={2} display="flex" alignItems="center">
        <SvgIcon
          fontSize="small"
          color="action"
          className={classes.attachmentsIcon}
        >
          <AttachmentsIcon />
        </SvgIcon>
        <Typography variant="subtitle2" color="textSecondary">
          {claim.documentCount || '0'}
        </Typography>
        <Box flexGrow={1} />
        <Chip
          clickable
          className={getStatusLabelClass(claim.claimStatus)}
          label={claim.claimStatus}
        />
      </Box>
    </Card>
  );
}

ClaimsCard.propTypes = {
  className: PropTypes.string,
  isLoading: PropTypes.bool,
  claim: PropTypes.object.isRequired,
  cardClickHandler: PropTypes.func
};

export default ClaimsCard;
